import type { TableColumn } from "@/lib/calc/table";

/**
 * Accessible results table: caption, scoped column headers, sticky header inside a scrollable (keyboard-focusable) region.
 * Printing shows every row (the print style below removes the height limit). Presentational: no gate, no export here.
 */
export function ResultsTable<Row>({ caption, rows, columns, rowKey, maxHeightClass = "max-h-[420px]" }: {
  caption: string; rows: Row[]; columns: TableColumn<Row>[]; rowKey: (row: Row) => string | number; maxHeightClass?: string;
}) {
  return (
    <div className="border rounded-xl overflow-hidden">
      <div className={`${maxHeightClass} overflow-y-auto print:max-h-none print:overflow-visible`} tabIndex={0} role="region" aria-label={`${caption} (scrollable)`}>
        <table className="w-full text-sm" data-testid="calc-table">
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 bg-card border-b">
            <tr className="text-left text-muted-foreground">
              {columns.map((c) => <th key={c.key} scope="col" className={`p-2 ${c.align === "right" ? "text-right" : ""}`}>{c.header}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={rowKey(r)} className="border-b last:border-0">
                {columns.map((c) => <td key={c.key} className={`p-2 ${c.align === "right" ? "text-right" : "whitespace-nowrap"}`}>{c.format ? c.format(r) : String(c.get(r) ?? "")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Print rule for a calculator report: when printing, only the element with this class is shown. */
export const CALC_PRINT_CSS = `@media print { body * { visibility: hidden !important; } .calc-print-area, .calc-print-area * { visibility: visible !important; } .calc-print-area { position: absolute; left: 0; top: 0; width: 100%; } }`;
